import React, { useState } from 'react';
import { IconButton, Popover, Typography, Box, Link } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useInRouterContext, useNavigate } from 'react-router-dom';
import { useMetricDefenses, methodologyHref, STATUS_LABELS } from '../utils/metricDefenses';

/**
 * Reusable inline info icon that opens a popover with a short explanation.
 *
 * Props:
 *   title       — string: bold heading inside the popover
 *   description — string | ReactNode: 1–3 sentence explanation
 *   learnMoreHref — optional string: anchor href for "Learn more →" link (e.g. "/page-guidelines#sales-velocity")
 *   howtoHref   — optional string: anchor href into the How-To guide (e.g. "/howto#entry-294")
 *   metricKey   — optional string: metric defense register key (api-python/docs/metric_defenses.yaml). The one-line
 *                 text comes from the register (overrides applied; `description` is the fallback while it loads) and
 *                 a "Why trust this number?" link opens that metric's explainer on the Methodology page.
 */
function RouterAwareLink({ href, children }) {
  const navigate = useNavigate();
  return (
    <Link
      href={href}
      onClick={(e) => { e.preventDefault(); navigate(href); }}
      variant="body2"
      underline="hover"
    >
      {children}
    </Link>
  );
}

export default function PageGuideTooltip({ title, description, learnMoreHref, howtoHref, metricKey }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const inRouter = useInRouterContext();
  const defenses = useMetricDefenses(Boolean(metricKey));
  const entry = metricKey && defenses?.byKey ? defenses.byKey[metricKey] : null;
  const text = entry?.info_bubble || description;

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        sx={{ ml: 0.5, p: 0.25, color: 'text.secondary', verticalAlign: 'middle' }}
        aria-label={`Info: ${title}`}
      >
        <InfoOutlinedIcon sx={{ fontSize: 16 }} />
      </IconButton>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, maxWidth: 320 } }}
      >
        {title && (
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
            {title}
          </Typography>
        )}
        <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
          {text}
        </Typography>
        {entry && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary' }}>
            {STATUS_LABELS[entry.status] || entry.status}
          </Typography>
        )}
        {metricKey && (
          <Box sx={{ mt: 1 }}>
            {inRouter ? (
              <RouterAwareLink href={methodologyHref(metricKey)}>Why trust this number? →</RouterAwareLink>
            ) : (
              <Link href={methodologyHref(metricKey)} variant="body2" underline="hover">Why trust this number? →</Link>
            )}
          </Box>
        )}
        {(learnMoreHref || howtoHref) && (
          <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {learnMoreHref && (
              <Link href={learnMoreHref} target="_blank" rel="noopener noreferrer" variant="body2" underline="hover">
                Learn more →
              </Link>
            )}
            {howtoHref && (
              <Link href={howtoHref} variant="body2" underline="hover">
                How-To guide →
              </Link>
            )}
          </Box>
        )}
      </Popover>
    </>
  );
}
